// Components
import { Box, Button, SvgIcon, Tooltip } from "@mui/material";
import { PlusCircle } from "react-feather";
import {
    type DisplayScenario,
    ScenarioDisplay,
    ScenarioDisplayWithFormik,
} from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioDisplay";
import { SpacerDisplay } from "src/views/marketing-actions/components/form/ScenarioCard/SpacerDiplay";

// Libs
import type { FieldInputProps, FormikErrors } from "formik";
import { useEffect, useRef, useState } from "react";

// Types
import type { TFunction } from "src/design-system/core/TranslationContext";

// Utils
import {
    MAX_EDGE_VALUE,
    MIN_EDGE_VALUE,
} from "src/views/marketing-actions/components/form/ScenarioCard/AddScenarioDialog";

// Hooks
import { isNil } from "lodash";
import { useScenarioCardStyles } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioCard.styles";
import { useIsOverflow } from "src/views/marketing-actions/components/form/ScenarioCard/ScenarioUtils";

export type DisplayElement =
    | { idx: number; type: "spacer" | "button" }
    | ({ idx: number; type: "scenario" } & DisplayScenario);

export type DisplayMode = "visualize" | "form";

interface ScenarioGridProps {
    t: TFunction;
    setIdDeleteScenario?: (id: number) => void;
    scenarios: DisplayScenario[];
    errorsOnScenarios?: FormikErrors<any>[];
    handleOpenDialogCreate?: (scenarioId: number) => void;
    handleOpenDialogUpdate?: (id: number) => void;
    isModificationAllowed?: boolean;
    channelField: FieldInputProps<any> | undefined;
    recurrenceField: FieldInputProps<any> | undefined;
    displayMode?: DisplayMode;
}

function ArrowUnderGrid() {
    const classes = useScenarioCardStyles();
    return (
        <div className={classes.arrowContainer}>
            <span className={classes.arrowLegendLess}>-</span>
            <span className={classes.arrowLegendMore}>+</span>
            <div className={classes.arrowLeftUnderGrid}> </div>
            <div className={classes.arrowMiddleUnderGrid}></div>
            <div className={classes.arrowRightUnderGrid}> </div>
        </div>
    );
}

export function ScenarioGrid({
    t,
    displayMode = "form",
    setIdDeleteScenario = function () {
        return;
    },
    scenarios,
    handleOpenDialogCreate = function () {
        return;
    },
    handleOpenDialogUpdate = function () {
        return;
    },
    channelField,
    errorsOnScenarios,
    recurrenceField,
    isModificationAllowed = true,
}: ScenarioGridProps): JSX.Element {
    const ref = useRef();
    const isOverflow = useIsOverflow(ref);
    const classes = useScenarioCardStyles();

    const [, setScroll] = useState({
        right: isOverflow ? 1 : 0,
        left: 0,
    });

    useEffect(() => {
        setScroll((current) => {
            return {
                ...current,
                right: isOverflow ? 1 : 0,
            };
        });
    }, [scenarios, isOverflow]);

    const displayElements: DisplayElement[] = [];
    for (let idx = 0; idx < scenarios.length; idx++) {
        const scenario = scenarios[idx];
        const leftScenario = scenarios[idx - 1];
        const rightScenario = scenarios[idx + 1];

        if (
            idx === 0 &&
            scenario.triggerMin !== MIN_EDGE_VALUE &&
            scenario.triggerMin !== leftScenario?.triggerMax &&
            scenarios.length < 6
        ) {
            displayElements.push({ idx: idx - 0.5, type: "button" });
        }

        displayElements.push({ idx, type: "scenario", ...scenario });

        if (
            scenario.triggerMax !== MAX_EDGE_VALUE &&
            scenario.triggerMax !== rightScenario?.triggerMin
        ) {
            if (scenarios.length < 6 && !rightScenario) {
                displayElements.push({ idx: idx + 0.5, type: "button" });
            } else if (rightScenario) {
                displayElements.push({ idx: idx + 0.5, type: "spacer" });
            }
        }
    }

    const offerTimeFiled = () => {
        if (
            isNil(channelField?.value) ||
            isNil(recurrenceField?.value?.startTime) ||
            isNil(recurrenceField?.value?.endTime)
        ) {
            return false;
        }

        return true;
    };

    const generateToolTipButtonAddScenarioMessage = () => {
        if (
            channelField?.value === undefined ||
            channelField?.value === null ||
            offerTimeFiled() === false
        ) {
            return t("smart-tooltip-missing-setup-for-scenarios");
        }
        return "";
    };

    const tooltipDisableScenario = () => {
        if (displayMode === "visualize") {
            return "";
        }
        if (offerTimeFiled() === false) {
            return t("smart-tooltip-disable-scenario");
        }
        return "";
    };
    const isScenarioDisabled = tooltipDisableScenario() !== "";

    const hideButton = scenarios?.length === 6 || isModificationAllowed === false ? true : false;

    return (
        <Box>
            {scenarios.length === 0 && displayMode === "form" && (
                <Box className={classes.ScenarioGridButtonContainer}>
                    <Tooltip title={generateToolTipButtonAddScenarioMessage()}>
                        <div>
                            <Button
                                color="primary"
                                variant="outlined"
                                disabled={
                                    generateToolTipButtonAddScenarioMessage() === "" ? false : true
                                }
                                startIcon={
                                    <SvgIcon fontSize="small">
                                        <PlusCircle />
                                    </SvgIcon>
                                }
                                onClick={() => handleOpenDialogCreate(0)}
                            >
                                {t("smart-create-button")}
                            </Button>
                        </div>
                    </Tooltip>
                </Box>
            )}

            <Box position="relative">
                {/* Y'a un bug avec les fades effects qui bloque le click au bouton, le retrait c'est un hotfix pour trouver le soucis plus tard\ */}
                {/* {Math.round(scroll.left) !== 0 && <Box className={classes.gridFadeEffectLeft}></Box>} */}
                {/* {Math.round(scroll.right) !== 0 && <Box className={classes.gridFadeEffectRight}></Box>} */}
                {scenarios.length > 0 && (
                    <div
                        ref={ref}
                        className={classes.gridContainer}
                        onScroll={(e) => {
                            setScroll({
                                left: e.currentTarget.scrollLeft,
                                right:
                                    e.currentTarget.scrollWidth -
                                    e.currentTarget.scrollLeft -
                                    e.currentTarget.clientWidth,
                            });
                        }}
                    >
                        {displayElements.map((el, index) => {
                            if (el.type === "button") {
                                return (
                                    <SpacerDisplay
                                        tooltipText={tooltipDisableScenario()}
                                        key={`${index}-spacer-button`}
                                        isDisabled={offerTimeFiled() === false}
                                        hideButton={hideButton}
                                        background={false}
                                        handleOpenDialogCreate={() => {
                                            if (offerTimeFiled() === false) {
                                                return;
                                            }
                                            handleOpenDialogCreate(el.idx + 0.5);
                                        }}
                                    />
                                );
                            }
                            if (el.type === "spacer") {
                                return (
                                    <SpacerDisplay
                                        key={`${index}-spacer`}
                                        hideButton={hideButton}
                                        tooltipText={tooltipDisableScenario()}
                                        isDisabled={offerTimeFiled() === false}
                                        background={true}
                                        handleOpenDialogCreate={() => {
                                            if (offerTimeFiled() === false) {
                                                return;
                                            }
                                            handleOpenDialogCreate(el.idx + 0.5);
                                        }}
                                    />
                                );
                            }
                            if (el.type === "scenario" && displayMode === "form") {
                                return (
                                    <ScenarioDisplayWithFormik
                                        t={t}
                                        isModificationAllowed={isModificationAllowed}
                                        key={`${index}-scenario`}
                                        scenario={el}
                                        error={(errorsOnScenarios || [])[index]}
                                        setIdDeleteScenario={setIdDeleteScenario}
                                        handleOpenDialogUpdate={handleOpenDialogUpdate}
                                        tooltipDisableScenario={tooltipDisableScenario}
                                        isScenarioDisabled={isScenarioDisabled}
                                        scenarioList={scenarios}
                                    />
                                );
                            }
                            if (el.type === "scenario" && displayMode === "visualize") {
                                return (
                                    <ScenarioDisplay
                                        t={t}
                                        isModificationAllowed={isModificationAllowed}
                                        key={`${index}-scenario`}
                                        scenario={el}
                                        error={(errorsOnScenarios || [])[index]}
                                        setIdDeleteScenario={setIdDeleteScenario}
                                        handleOpenDialogUpdate={handleOpenDialogUpdate}
                                        tooltipDisableScenario={tooltipDisableScenario}
                                        isScenarioDisabled={isScenarioDisabled}
                                        scenarioList={scenarios}
                                    />
                                );
                            }
                            return null;
                        })}
                    </div>
                )}
                {scenarios.length > 0 && <ArrowUnderGrid />}
            </Box>
        </Box>
    );
}
